import type { FormEvent } from "react";
import type { SignUpStep } from "./step";
import type { UrlFragmentData } from "./url";
import { bound, observable } from "ecce-react";
import { CompanyDetailsStep, AlreadySubscribedStep, CollectEmailAddressStep, CollectQuantityStep, ThankYouStep, UserDetailsStep, VerifyEmailAddressStep } from "./steps";
import { Api } from "./api";
import { SignUpRequestState } from "./sign-up-request";
import { parseUrlFragment } from "./url";
import { AddressDetailsStep } from "./steps/5-address-details-step";


export class App {
	static readonly DIALOG_URL_FRAGMENT = "#signup";

	readonly api: Api;

	#data: UrlFragmentData | null = null;
	get data(): UrlFragmentData | null { return this.#data; }
	@observable() private set data(value: UrlFragmentData | null) { this.#data = value; }

	readonly state = new SignUpRequestState();

	readonly #quantityStep: CollectQuantityStep;
	readonly #collectEmailAddressStep: CollectEmailAddressStep;
	readonly #verifyEmailAddressStep: VerifyEmailAddressStep;
	readonly #userDetailsStep: UserDetailsStep;
	readonly #companyDetailsStep: CompanyDetailsStep;
	readonly #addressDetailsStep: AddressDetailsStep;
	readonly #thankYouStep: ThankYouStep;
	readonly #alreadySubscribedStep: AlreadySubscribedStep;

	readonly steps: readonly SignUpStep[];
	#activeStep: SignUpStep | null = null;
	get activeStep(): SignUpStep | null { return this.#activeStep; }
	@observable() private set activeStep(value: SignUpStep | null) { this.#activeStep = value; }

	#verifiedEmailAddress: string | null = null;

	#submitting: boolean = false;
	get submitting(): boolean { return this.#submitting; }
	@observable() private set submitting(value: boolean) { this.#submitting = value; }

	constructor() {
		this.api = new Api(window.__purser__.ajaxUrl);
		this.steps = [
			this.#quantityStep = new CollectQuantityStep(this),
			this.#collectEmailAddressStep = new CollectEmailAddressStep(this),
			this.#verifyEmailAddressStep = new VerifyEmailAddressStep(this),
			this.#userDetailsStep = new UserDetailsStep(this, this.#collectEmailAddressStep),
			this.#companyDetailsStep = new CompanyDetailsStep(this, this.#userDetailsStep),
			this.#addressDetailsStep = new AddressDetailsStep(this, this.#companyDetailsStep),
			this.#thankYouStep = new ThankYouStep(this),
			this.#alreadySubscribedStep = new AlreadySubscribedStep(this),
		];

		window.addEventListener("hashchange", this.#handleHashChange);
		this.#handleHashChange();
	}

	#handleHashChange = () => {
		const nextData = parseUrlFragment(window.location.hash);
		if(nextData) {
			this.state.attribute("plan").value = App.parsePlan(nextData);
			switch(nextData.type) {
				case "central":
					this.state.attribute("quantity").value = nextData.paymentCycle === "monthly" ? 3 : 1;
					this.#activateStep(this.#quantityStep);
					break;
				default:
					this.state.attribute("quantity").value = null;
					this.#activateStep(this.#collectEmailAddressStep);
					break;
			}
		} else {
			this.#activateStep(null);
		}

		this.data = nextData;
	};

	get emailAddress(): string {
		const emailAddress = this.state.attribute("email").value;
		if(!emailAddress) {
			throw new Error("No email address has been collected.");
		}

		return emailAddress;
	}

	get quantity(): number | null {
		if(this.data?.type !== "central") {
			return null;
		}

		return this.state.attribute("quantity").value;
	}

	onQuantitySelected(): void {
		this.#activateStep(this.#collectEmailAddressStep);
	}

	onVerificationHashReceived(hash: string): void {
		this.#verifyEmailAddressStep.setHash(hash);
		this.#activateStep(this.#verifyEmailAddressStep);
	}

	onEmailVerified(): void {
		this.#verifiedEmailAddress = this.emailAddress;
		this.#activateStep(this.#userDetailsStep);
	}

	onUserDetailsProvided(): void {
		this.#activateStep(this.#companyDetailsStep);
	}

	onCompanyDetailsProvided(): void {
		this.#activateStep(this.#addressDetailsStep);
	}

	async onAddressDetailsProvided(): Promise<void> {
		await this.#createSignUpRequest();
	}

	async #createSignUpRequest(): Promise<void> {
		const response = await this.api.createSignUpRequest(this.state.make());
		if(!response.ok) {
			console.error("Bad response:", response); // eslint-disable-line no-console
			alert(response.error.message); // eslint-disable-line no-alert
			return;
		}

		if(response.data.subscribed) {
			this.#onAlreadySubscribed();
		} else {
			this.#onSignUpRequestCreated();
		}
	}

	#onSignUpRequestCreated(): void {
		this.#activateStep(this.#thankYouStep);
	}

	#onAlreadySubscribed(): void {
		this.#activateStep(this.#alreadySubscribedStep);
	}

	isEmailAddressVerified(emailAddress: string | undefined): boolean {
		return emailAddress === this.#verifiedEmailAddress;
	}

	get previousStep(): SignUpStep | null {
		return this.#activeStep?.previousStep ?? null;
	}

	@bound()
	goBack(): void {
		if(!this.previousStep) {
			return;
		}

		this.#activateStep(this.previousStep);
	}

	get canChangeEmailAddress(): boolean {
		return this.activeStep !== this.#quantityStep && this.activeStep !== this.#collectEmailAddressStep && this.activeStep !== this.#thankYouStep;
	}

	get canSubmit(): boolean {
		return this.activeStep !== this.#thankYouStep && this.activeStep !== this.#alreadySubscribedStep;
	}


	@bound()
	changeEmailAddress(): void {
		if(!this.canChangeEmailAddress) {
			return;
		}

		this.#activateStep(this.#collectEmailAddressStep);
	}

	#activateStep(step: SignUpStep | null): void {
		this.activeStep = step;
	}

	@bound()
	handleFormSubmitted(ev: FormEvent<HTMLFormElement>): void {
		ev.preventDefault();
		if(!this.activeStep || this.submitting) {
			return;
		}

		const data = new FormData(ev.currentTarget);

		const params: Record<string, string> = {};
		data.forEach((value, key) => params[key] = value.toString());

		this.submitting = true;
		this.activeStep["handleSubmit"]()
			.finally(() => this.submitting = false);
	}

	@bound()
	closeDialog(): void {
		window.location.hash = "_";
	}

	static parsePlan(data: UrlFragmentData): string | null {
		if(!data?.plan && !data?.type) {
			return null;
		}

		const segments: string[] = [];
		if(data.type) {
			segments.push("[" + data.type + "]");
		}
		if(data.plan) {
			segments.push(data.plan);
		}

		return segments.join(" ");
	}
}
